import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoTubePlate,
  GeckoTubePlateJSON,
} from 'types/assetModel/features/geckoTubePlate'
import { validateTubeMeasurement } from 'types/assetModel/features/legacyBoiler/geckoLegacyBoilerWall'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredIntSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

type HoodStraightSupportedFeatures =
  | GeckoTubePlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON

export type HoodStraightAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.HoodStraight
  radius: number
  length: number
  tube_count: number
  tubes: {
    tube_diameter: number | undefined
    tube_spacing: number | undefined
  }
  material: string | undefined
  installation_date: string | undefined
  features: Record<string, HoodStraightSupportedFeatures>
  scene_config: SceneConfig
  component_bounds: {
    tube_start: number
    tube_end: number
    y_start: number
    y_end: number
  }
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  compass_orientation?: number
}

export const HoodStraightAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.TubePlate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
]

export const NewHoodStraightAssetModelJSON = {
  name: 'New Hood Straight',
  type: GeckoComponentModelType.HoodStraight,
  radius: 0,
  length: 0,
  tube_count: 0,
  tubes: {
    tube_diameter: undefined,
    tube_spacing: undefined,
  },
  material: undefined,
  installation_date: undefined,
  features: {},
  scene_config: {},
  component_bounds: {
    tube_start: 0,
    y_start: 0,
    tube_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  compass_orientation: undefined,
  nominal: {},
} as HoodStraightAssetModelJSON

export class GeckoHoodStraight extends GeckoEditableComponent {
  type: GeckoComponentModelType.HoodStraight
  radius: GeckoMeasurement
  length: GeckoMeasurement
  tubeCount: GeckoMeasurement
  tubeDiameter: GeckoMeasurement | undefined
  tubeSpacing: GeckoMeasurement | undefined
  material: string | undefined
  installationDate: Date | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  plates: Map<string, GeckoTubePlate>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    length: GeckoMeasurement,
    tubeCount: GeckoMeasurement,
    tubeDiameter: GeckoMeasurement | undefined,
    tubeSpacing: GeckoMeasurement | undefined,
    material: string | undefined,
    installationDate: Date | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    plates: Map<string, GeckoTubePlate>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.HoodStraight
    this.radius = radius
    this.length = length

    validateTubeMeasurement(tubeCount, id, 'GeckoHoodStraight.tube_count')
    this.tubeCount = tubeCount
    this.tubeDiameter = tubeDiameter
    this.tubeSpacing = tubeSpacing

    this.material = material
    this.installationDate = installationDate
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public static CreateFromJSON(id: string, json: HoodStraightAssetModelJSON) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const hoodRadius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )
    const hoodLength = GeckoMeasurement.CreateFromValue(
      json.length as number,
      GeckoMeasurementUnit.Meters
    )
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.tube_start,
      componentBoundsData.y_start,
      componentBoundsData.tube_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Tubes,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      HoodStraightSupportedFeatures
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.TubePlate
    })
    const plates = GeckoTubePlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoTubePlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const tubeCount = GeckoMeasurement.CreateFromValue(
      json.tube_count,
      GeckoMeasurementUnit.Tubes
    )
    const tubes = json.tubes as Record<string, number>
    const tubeSpacing =
      tubes.tube_spacing === undefined
        ? undefined
        : GeckoMeasurement.CreateFromValue(
            tubes.tube_spacing,
            GeckoMeasurementUnit.Meters
          )
    const tubeDiameter =
      tubes.tube_diameter === undefined
        ? undefined
        : GeckoMeasurement.CreateFromValue(
            tubes.tube_diameter,
            GeckoMeasurementUnit.Meters
          )

    return new GeckoHoodStraight(
      id,
      json,
      name,
      hoodRadius,
      hoodLength,
      tubeCount,
      tubeDiameter,
      tubeSpacing,
      material,
      installationDate,
      componentBounds,
      sceneConfig,
      plates,
      verticalWelds,
      horizontalWelds,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): HoodStraightAssetModelJSON {
    return generateHoodStraight(
      this.name,
      this.radius,
      this.length,
      this.tubeCount,
      this.tubeDiameter,
      this.tubeSpacing,
      this.material,
      this.installationDate,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as HoodStraightAssetModelJSON
    )
  }
}

const generateHoodStraight = (
  name: string,
  radius: GeckoMeasurement,
  length: GeckoMeasurement,
  tubeCount: GeckoMeasurement,
  tubeDiameter: GeckoMeasurement | undefined,
  tubeSpacing: GeckoMeasurement | undefined,
  material: string | undefined,
  installationDate: Date | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherHoodStraightData: HoodStraightAssetModelJSON = NewHoodStraightAssetModelJSON
): HoodStraightAssetModelJSON => {
  const hoodStraightLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const hoodStraightRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  const hoodTubeSpacing =
    tubeSpacing !== undefined
      ? tubeSpacing
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined
  const hoodTubeDiameter =
    tubeDiameter !== undefined
      ? tubeDiameter
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.HoodStraight,
    radius: hoodStraightRadius,
    length: hoodStraightLength,
    tube_count: tubeCount.num,
    tubes: {
      tube_diameter: hoodTubeDiameter,
      tube_spacing: hoodTubeSpacing,
    },
    material: material,
    installation_date: parsedInstallationDate,
    features: otherHoodStraightData.features,
    scene_config: otherHoodStraightData.scene_config,
    component_bounds: {
      tube_start: 1,
      y_start: 0,
      tube_end: tubeCount.num,
      y_end: hoodStraightLength,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const HoodStraightAssetModelFormFields = (
  hoodStraightJSON: HoodStraightAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Hood Straight Name',
      value: hoodStraightJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'radius',
      label: 'Radius',
      value: hoodStraightJSON.radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: hoodStraightJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_count',
      label: 'Tube Count',
      value: hoodStraightJSON.tube_count,
      unit_magnitude: GeckoMeasurementUnit.Tubes,
      validationRule: requiredIntSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_spacing',
      label: 'Tube Spacing',
      value: hoodStraightJSON.tubes.tube_spacing,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_diameter',
      label: 'Tube Diameter',
      value: hoodStraightJSON.tubes.tube_diameter,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: hoodStraightJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(hoodStraightJSON.installation_date, true),
    wallThicknessFormField(hoodStraightJSON.nominal.wall_thickness),
    tminFormField(hoodStraightJSON.tmin),
    compassOrientationFormField(hoodStraightJSON.compass_orientation),
  ]
}

export const HoodStraightJSONFromFormFields = (
  component: HoodStraightAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  const parsedTubeSpacing = parseNumber(
    getFormFieldByDataKey(formFields, 'tube_spacing')?.value
  )
  const parsedTubeDiameter = parseNumber(
    getFormFieldByDataKey(formFields, 'tube_diameter')?.value
  )
  return generateHoodStraight(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'tube_count')?.value as number,
      GeckoMeasurementUnit.Tubes
    ),
    parsedTubeDiameter !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTubeDiameter,
          GeckoMeasurementUnit.Tubes
        )
      : undefined,
    parsedTubeSpacing !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTubeSpacing,
          GeckoMeasurementUnit.Tubes
        )
      : undefined,
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
